import { Key, ReactNode, useCallback } from 'react';
import { Chip, TmdxChip, Image, SortDescriptor, Tooltip, Button } from '@ds';
import { TableColumnType } from '@ui';
import { Link, useNavigate } from 'react-router-dom';
import { InvoiceStatusChips } from '@features/components/CaseOverview/components/Invoices/components/InvoiceStatusChips';
import { compareAsc } from 'date-fns';
import { MdOutlineShare } from 'react-icons/md';
import { caseSectionTabs, useCaseSectionTabsStore } from '@features/store/case-section-tabs.store';
import { formatDate, getColorsByOrganType, getNameValuesFromEnumNames, getOrganImg } from '@/utils/helpers';
import {
  ArchiveCaseListFilters,
  ArchivedCase,
  CaseStatus,
  CustomerCaseStatus,
  DonorType,
  Invoice,
  InvoiceState,
  InvoiceStatus,
  OrganType,
  OutcomeType,
  PerfusionTerminationOption,
  PerfusionTerminationType,
  RandomizationFilterType,
  TimestampWithZone,
} from '@/api/apis/case';
import {
  caseOutcomeNames,
  caseStatusNames,
  invoiceStateNames,
  invoiceStatusNames,
  outcomeTypeNames,
  randomizationFilterTypeNames,
} from '@/constants/case/case.constants';
import { DEFAULT_DATE_FORMAT_STRING } from '@/constants/date.constants';
import { isInvoiceSupported } from '@/utils/helpers/country-validation.helpers';
import { ServiceRegionId } from '@/constants/serviceRegion.constants';

export type ArchiveCaseFilters = {
  organType?: string;
  donorType?: string;
  caseOutcomes?: string;
};

export type ArchiveFilterConfig = {
  statusList: { value: CaseStatus | string; name: string }[];
  outcomeList: { value: OutcomeType; name: string }[];
};

export type ArchiveCasesFiltersState = {
  organType: OrganType[];
  donorType: DonorType[];
  caseStatuses: CaseStatus[];
  outcomeType: OutcomeType[];
  caseOutcome: CaseStatus[];
  txCenterIds: string[];
  opoIds: string[];
  serviceRequestDateTimeFrom: string;
  serviceRequestDateTimeTo: string;
  outcomeDateTimeFrom: string;
  outcomeDateTimeTo: string;
  offOCSDateTimeFrom: string;
  offOCSDateTimeTo: string;
  hasInvoice: InvoiceState[];
  invoiceStatuses: InvoiceStatus[];
  customerCaseStatuses?: CustomerCaseStatus[];
  trialProgramIds: string[];
  randomizationResultFilter: RandomizationFilterType[];
  serviceRegionIds: ServiceRegionId[];
  opoRequest?: boolean;
  isReallocatedFromSource?: boolean;
};

export const STATUS_SELECT_LIST = [
  {
    value: CaseStatus.Closed,
    name: caseOutcomeNames[CaseStatus.Closed],
  },
  {
    value: CaseStatus.Finished,
    name: caseOutcomeNames[CaseStatus.Finished],
  },
];

export const OUTCOME_SELECT_LIST = [
  {
    value: OutcomeType.Aborted,
    name: outcomeTypeNames[OutcomeType.Aborted],
  },
  {
    value: OutcomeType.DryRun,
    name: outcomeTypeNames[OutcomeType.DryRun],
  },
  {
    value: OutcomeType.Transplant,
    name: outcomeTypeNames[OutcomeType.Transplant],
  },
  {
    value: OutcomeType.Turndown,
    name: outcomeTypeNames[OutcomeType.Turndown],
  },
];

export const OUTCOME_STATUS_SELECT_LIST = [
  {
    value: CaseStatus.ClinicalScreening,
    name: caseStatusNames[CaseStatus.ClinicalScreening],
  },
  {
    value: CaseStatus.Declined,
    name: caseStatusNames[CaseStatus.Declined],
  },
  {
    value: CaseStatus.ServiceDeclined,
    name: caseStatusNames[CaseStatus.ServiceDeclined],
  },
  {
    value: 'ServiceCompleted',
    name: 'Service Completed',
  },
];

export const INVOICE_STATE_SELECT_LIST = [
  {
    value: InvoiceState.NoInvoice,
    name: invoiceStateNames[InvoiceState.NoInvoice],
  },
  {
    value: InvoiceState.InvoiceAdded,
    name: invoiceStateNames[InvoiceState.InvoiceAdded],
  },
];

export const INVOICE_STATUS_SELECT_LIST = [
  {
    value: InvoiceStatus.Open,
    name: invoiceStatusNames[InvoiceStatus.Open],
  },
  {
    value: InvoiceStatus.PartiallyCredited,
    name: invoiceStatusNames[InvoiceStatus.PartiallyCredited],
  },
  {
    value: InvoiceStatus.FullyCredited,
    name: invoiceStatusNames[InvoiceStatus.FullyCredited],
  },
  {
    value: InvoiceStatus.PartiallyPaid,
    name: invoiceStatusNames[InvoiceStatus.PartiallyPaid],
  },
  {
    value: InvoiceStatus.Paid,
    name: invoiceStatusNames[InvoiceStatus.Paid],
  },
];

export const RANDOMIZATION_FILTER_SELECT_LIST = getNameValuesFromEnumNames(randomizationFilterTypeNames);

export const ARCHIVE_CASES_LIST_FILTERS_BASE: ArchiveCasesFiltersState = {
  organType: [],
  donorType: [],
  caseStatuses: [],
  outcomeType: [],
  caseOutcome: [],
  txCenterIds: [],
  opoIds: [],
  hasInvoice: [],
  invoiceStatuses: [],
  trialProgramIds: [],
  randomizationResultFilter: [],
  serviceRequestDateTimeFrom: '',
  serviceRequestDateTimeTo: '',
  outcomeDateTimeFrom: '',
  outcomeDateTimeTo: '',
  offOCSDateTimeFrom: '',
  offOCSDateTimeTo: '',
  serviceRegionIds: [],
  isReallocatedFromSource: undefined,
  opoRequest: undefined,
};

export const ARCHIVE_CASES_LIST_FILTERS_BASE_CP: ArchiveCaseListFilters = {
  organType: [],
  donorType: [],
  outcomeType: [],
  caseOutcome: [],
  hasInvoice: [],
  invoiceStatuses: [],
  serviceRequestDateTimeFrom: [],
  serviceRequestDateTimeTo: [],
  outcomeDateTimeFrom: [],
  outcomeDateTimeTo: [],
  offOCSDateTimeFrom: [],
  offOCSDateTimeTo: [],
  trialProgramIds: [],
  randomizationResultFilter: [],
  customerCaseStatuses: [],
  txCenterIds: [],
  opoIds: [],
  caseStatuses: [],
  serviceRegionIds: [],
};

export const FILTERS_NAMES = {
  organType: 'Organ',
  donorType: 'Donor Type',
  caseStatuses: 'Case Status',
  outcomeType: 'Outcome',
  caseOutcome: 'Outcome Status',
  txCenterIds: 'TX Center',
  opoIds: 'OPO',
  hasInvoice: 'Invoice',
  invoiceStatuses: 'Invoice Status',
  serviceDates: 'Service Dates',
  outcomeDates: 'Outcome Dates',
  offOCSDates: 'Off OCS/SCS Handoff Dates',
  offOCSDatesNoTrials: 'Off OCS Dates',
  trialProgramIds: 'Trial Programs',
  randomizationResultFilter: 'Randomization Outcome',
  serviceRegionIds: 'Service Region',
  caseType: 'Case Type',
} as const;

export const DEFAULT_SORT_BY: SortDescriptor = {
  column: 'clinicalScreeningDateTime',
  direction: 'descending',
};

export const getArchiveCasesTableColumnsLP = (hasAccessPermissions?: boolean): TableColumnType[] => {
  const columns: TableColumnType[] = [
    {
      key: 'CaseName',
      label: 'Case',
      isSortable: false,
      minWidthClassName: 'min-w-[162px]',
    },
    {
      key: 'donorType',
      label: 'Donor Type',
      isSortable: true,
      minWidthClassName: 'min-w-[131px]',
    },
    {
      key: 'status',
      label: 'Case Status',
      isSortable: false,
      minWidthClassName: 'min-w-[107px]',
    },
    {
      key: 'TrialProgramName',
      label: 'Trial Programs',
      isSortable: true,
      minWidthClassName: 'min-w-[107px]',
    },
    {
      key: 'randomization',
      label: 'Randomization Outcome',
      isSortable: false,
      minWidthClassName: 'min-w-[107px]',
    },
    {
      key: 'outcome',
      label: 'outcome / status',
      isSortable: false,
      minWidthClassName: 'min-w-[172px]',
      tooltipText: 'Status the Outcome has been reported at',
    },
    {
      key: 'txCenter',
      label: 'tx center',
      isSortable: false,
      minWidthClassName: 'min-w-[120px]',
    },
    {
      key: 'clinicalScreeningDateTime',
      label: 'service request date',
      isSortable: true,
      minWidthClassName: 'min-w-[180px]',
    },
    {
      key: 'outcomeDateTime',
      label: 'outcome date',
      isSortable: true,
      minWidthClassName: 'min-w-[180px]',
    },
    {
      key: 'OffOCSDateTime',
      label: 'Off OCS/SCS Handoff Date',
      isSortable: true,
      minWidthClassName: 'min-w-[180px]',
    },
    {
      key: 'invoice',
      label: 'Invoice',
      isSortable: false,
      minWidthClassName: 'w-[83px]',
    },
  ];

  if (hasAccessPermissions) {
    columns.push({
      key: 'actions',
      label: 'Actions',
      isSortable: false,
      minWidthClassName: 'min-w-11',
    });
  }

  return columns;
};

export const getArchiveCasesTableColumnsCP = (
  countryCode: string,
  participatesInTrials: boolean = false,
): TableColumnType[] => {
  const baseColumns: TableColumnType[] = [
    {
      key: 'CaseName',
      label: 'UNOSID',
      isSortable: false,
      minWidthClassName: 'min-w-[162px]',
    },
    {
      key: 'donorType',
      label: 'Donor Type',
      isSortable: true,
      minWidthClassName: 'min-w-[131px]',
    },
    {
      key: 'outcome',
      label: 'outcome / status',
      isSortable: false,
      minWidthClassName: 'min-w-[172px]',
      tooltipText: 'Status the Outcome has been reported at',
    },
    {
      key: 'clinicalScreeningDateTime',
      label: 'service request date, time',
      isSortable: true,
      minWidthClassName: 'min-w-[180px]',
    },
    {
      key: 'outcomeDateTime',
      label: 'outcome date, time',
      isSortable: true,
      minWidthClassName: 'min-w-[180px]',
    },
    {
      key: 'OffOCSDateTime',
      label: participatesInTrials ? 'Off OCS/SCS Handoff Date' : 'Off OCS Date',
      isSortable: true,
      minWidthClassName: 'min-w-[180px]',
    },
  ];

  const trialColumns: TableColumnType[] = participatesInTrials
    ? [
        {
          key: 'TrialProgramName',
          label: 'Trial Programs',
          isSortable: true,
          minWidthClassName: 'min-w-[107px]',
        },
        {
          key: 'randomization',
          label: 'Randomization Outcome',
          isSortable: false,
          minWidthClassName: 'min-w-[107px]',
        },
      ]
    : [];

  const invoiceColumn: TableColumnType[] = isInvoiceSupported(countryCode)
    ? [
        {
          key: 'invoice',
          label: 'Invoice',
          isSortable: false,
          minWidthClassName: 'w-[83px]',
        },
      ]
    : [];

  return [...baseColumns, ...trialColumns, ...invoiceColumn];
};

const getInvoiceRow = (invoices: Invoice[]) => {
  if (!invoices.length) return null;

  const statusOrder = {
    [InvoiceStatus.Open]: 1,
    [InvoiceStatus.PartiallyCredited]: 2,
    [InvoiceStatus.FullyCredited]: 3,
    [InvoiceStatus.PartiallyPaid]: 4,
    [InvoiceStatus.Paid]: 5,
  };

  const sortedInvoices = invoices.sort((a, b) => {
    return statusOrder[a.status] - statusOrder[b.status];
  });

  return sortedInvoices.map((item) => (
    <div className="flex items-center justify-between gap-4 w-full" key={item.id}>
      <span className="text-s-sb leading-none">{item.fileName}</span>
      <InvoiceStatusChips status={item.status} />
    </div>
  ));
};

const getOffOcsDateTime = ({ offOCSData, organType }: ArchivedCase) => {
  if (!offOCSData) return null;

  if (organType === OrganType.Lungs) {
    const item = offOCSData?.item as PerfusionTerminationOption;
    let timestamp;
    let timeZone;

    if (item[PerfusionTerminationType.doubleLungFlush]) {
      [timestamp, timeZone] = [
        item[PerfusionTerminationType.doubleLungFlush]?.flushTime?.timestamp,
        item[PerfusionTerminationType.doubleLungFlush]?.flushTime?.timeZone,
      ];
    }

    if (item[PerfusionTerminationType.sequentialLungFlush]) {
      const [firstFlushTime, secondFlushTime] = [
        item[PerfusionTerminationType.sequentialLungFlush]?.firstLungFlushTime,
        item[PerfusionTerminationType.sequentialLungFlush]?.secondLungFlushTime,
      ];

      if (firstFlushTime && secondFlushTime) {
        const isFirstFlushLater =
          compareAsc(new Date(firstFlushTime.timestamp), new Date(secondFlushTime.timestamp)) > 0;
        [timestamp, timeZone] = [
          isFirstFlushLater ? firstFlushTime.timestamp : secondFlushTime.timestamp,
          isFirstFlushLater ? firstFlushTime.timeZone : secondFlushTime.timeZone,
        ];
      }
    }

    return formatDate(timestamp, DEFAULT_DATE_FORMAT_STRING, {
      timezone: timeZone,
    });
  }

  const item = offOCSData?.item as TimestampWithZone;
  return formatDate(item?.timestamp, DEFAULT_DATE_FORMAT_STRING, {
    timezone: item?.timeZone,
  });
};

const AccessButton = ({ caseId, isDisabled }: { caseId: string; isDisabled: boolean }) => {
  const navigate = useNavigate();
  const setActiveSectionTab = useCaseSectionTabsStore((s) => s.setActiveSectionTab);

  const onClick = useCallback(() => {
    setActiveSectionTab(caseId, caseSectionTabs.ACCESS);
    navigate(`/case/${caseId}/access`);
  }, [caseId, navigate, setActiveSectionTab]);

  return (
    <Button
      isDisabled={isDisabled}
      className="text-s-sb"
      size="md"
      variant="outline"
      startContent={<MdOutlineShare size={18} />}
      onClick={onClick}
    >
      Access
    </Button>
  );
};

export const getRenderArchiveCasesTableCell =
  (
    hasViewInvoicePermission: boolean,
    caseDetailsRedirectUrl: string,
    onInvoiceModalOpen: (invoices: Invoice[], caseId: string) => void,
  ) =>
  (item: ArchivedCase, columnKey: Key): ReactNode => {
    const {
      organType,
      donorType,
      invoices,
      caseId,
      outcomeData,
      caseName,
      caseOutcome,
      status,
      txCenterDetails,
      clinicalScreeningDateTime,
      trialProgram,
      randomization,
    } = item;

    const colors = getColorsByOrganType(organType);
    const offOcsDateTime = getOffOcsDateTime(item);
    const isInvoiceNotEmpty = invoices.length > 0;
    const invoiceChipText = `${invoices.length} File${invoices.length ? '' : 's'}`;
    const caseIdPathParam: string = ':caseId';
    let caseStatusName = 'Service Completed';

    if (caseOutcome) {
      caseStatusName = (caseOutcome as string) !== 'ServiceCompleted' ? caseStatusNames[caseOutcome] : caseStatusName;
    }

    switch (columnKey) {
      case 'CaseName':
        return (
          <Link to={caseDetailsRedirectUrl.replace(caseIdPathParam, caseId)}>
            <span className="text-m-m text-base-5 cursor-pointer">{caseName || '-'}</span>
          </Link>
        );
      case 'donorType':
        return (
          <div className="flex items-center">
            <Chip className={`${colors.bg40} text-s-m text-base-5`}>{donorType ?? '-'}</Chip>
            <div className={`${colors.bg40} flex items-center justify-center rounded-2xl w-6 h-6`}>
              <Image src={getOrganImg(organType)} />
            </div>
          </div>
        );
      case 'status':
        return <TmdxChip size="sm">{status}</TmdxChip>;
      case 'TrialProgramName':
        return <span className="text-m-r">{trialProgram?.name || '-'}</span>;
      case 'randomization':
        return (
          <span className="text-m-r">
            {randomization?.result ||
              (trialProgram ? randomizationFilterTypeNames[RandomizationFilterType.NotRandomized] : '-')}
          </span>
        );
      case 'outcome':
        return (
          <div className="flex flex-col">
            <span className="text-m-r">
              {outcomeData && caseOutcome ? outcomeTypeNames[outcomeData.outcomeType] : '-'}
            </span>
            <span className="text-s-r text-base-20">{caseStatusName}</span>
          </div>
        );
      case 'txCenter':
        return <span className="text-m-r">{txCenterDetails?.name || '-'}</span>;
      case 'clinicalScreeningDateTime':
        return (
          <span className="text-m-r">
            {clinicalScreeningDateTime ? formatDate(clinicalScreeningDateTime, DEFAULT_DATE_FORMAT_STRING) : '-'}
          </span>
        );
      case 'outcomeDateTime':
        return (
          <span className="text-m-r">
            {outcomeData?.outcomeDateTime ? formatDate(outcomeData.outcomeDateTime, DEFAULT_DATE_FORMAT_STRING) : '-'}
          </span>
        );
      case 'OffOCSDateTime':
        return <span className="text-m-r">{offOcsDateTime || '-'}</span>;
      case 'invoice':
        return Array.isArray(invoices) && isInvoiceNotEmpty && hasViewInvoicePermission ? (
          <Tooltip
            content={getInvoiceRow(invoices)}
            placement="left"
            showArrow
            classNames={{
              content: 'py-2 px-4 justify-center gap-2',
            }}
          >
            <Button
              aria-label="Open invoice modal"
              className="min-w-max h-6 p-0 bg-transparent data-[hover=true]:bg-transparent data-[focus=true]:bg-transparent"
              onClick={() => onInvoiceModalOpen(invoices, caseId)}
            >
              <TmdxChip size="sm" className="mx-auto cursor-pointer">
                {invoiceChipText}
              </TmdxChip>
            </Button>
          </Tooltip>
        ) : null;

      case 'actions':
        return <AccessButton caseId={caseId} isDisabled={false} />;

      default:
        return null;
    }
  };
